import { SiteVariablesPrepared } from "@fluentui/react-northstar";
import { IChartData, IChartDataSet, IChartPatterns, IDraw } from "./chart-types";
import { formatDataNumber } from "../../helpers/format-numbers";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import i18n from "../../i18n";

export const random = (min: number, max: number): number =>
    Math.round(Math.random() * (max - min) + min);

export const chartAxisCallback = (value: number | string): string => {
    let suffixes = i18n.language == "pt-BR" ? ["mil", "mi"] : ["K", "M", "G", "T", "P", "E"];

    if (typeof value === "number") {
        if (value < 1000) {
            return String(value);
        }
        const exp = Math.floor(Math.log(Number(value)) / Math.log(1000));
        value = `${Number(value) / Math.pow(1000, exp)}${suffixes[exp - 1]}`;
        switch (value.length) {
            case 2:
                return value + "  ";
            case 1:
                return value + "   ";
            case 3:
            default:
                return value;
        }
    } else {
        return value;
    }
};

export const hexToRgb = (hex: string) => {
    if (hex.length < 6) {
        hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    }
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
            result[3],
            16
        )}`
        : null;
};

export const numberFormat = (value: number | string): string => {
    // Certificar-se de que o valor é um número
    const numValue = typeof value === 'number' ? value : parseFloat(value as string);

    if (isNaN(numValue)) {
        return String(value);  // Se o valor não for um número válido, retorna o valor original
    }

    return new Intl.NumberFormat('pt-BR').format(numValue);
};

export function tooltipTrigger({
    chart,
    data,
    set,
    index,
    siteVariables,
    mergeDuplicates,
    patterns,
}: {
    chart: any;
    data: IChartData;
    set: number;
    index: number;
    siteVariables: SiteVariablesPrepared;
    mergeDuplicates?: boolean;
    patterns?: (colorSheme: any) => IDraw[];
}) {
    const { theme, colorScheme } = siteVariables;
    if (mergeDuplicates) {
        const duplicates: number[] = [];
        const segments: any[] = [];
        // Check for equal data points
        data.datasets.filter((dataset: IChartDataSet, i: number) => {
            if (dataset.data[index] === data.datasets[set].data[index]) {
                duplicates.push(i);
            }
        });
        duplicates.forEach((segmentId) => {
            segments.push(chart.getDatasetMeta(segmentId).data[index]);
        });

        chart.tooltip._active = segments;
    } else {
        const segment = chart.getDatasetMeta(set).data[index];
        chart.tooltip._active = [segment];
    }
    chart.tooltip.update();
    chart.draw();
}

export const tooltipAxisYLine = ({ chart, ctx, tooltip }: any) => {
    if (tooltip._active && tooltip._active.length) {
        const activePoint = tooltip._active[0],
            y = activePoint.tooltipPosition().y,
            x = activePoint.tooltipPosition().x,
            y_axis = chart.scales["y-axis-0"],
            topY = y_axis.top,
            bottomY = y_axis.bottom;

        ctx.save();

        // Line
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.setLineDash([5, 5]);
        ctx.lineWidth = 1;
        ctx.strokeStyle = chart.options.scales.yAxes[0].gridLines.color;
        ctx.stroke();
        // Point
        ctx.beginPath();
        ctx.setLineDash([]);
        ctx.arc(x, y, 5, 0, 2 * Math.PI, true);
        ctx.lineWidth = 2;
        ctx.fillStyle = "white";
        ctx.strokeStyle =
            chart.data.datasets[activePoint._datasetIndex].hoverBorderColor;
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
    }
};

export const tooltipAxisXLine = ({ chart, ctx, tooltip }: any) => {
    if (tooltip._active && tooltip._active.length) {
        const activePoint = tooltip._active[0],
            y = activePoint.tooltipPosition().y,
            x = activePoint.tooltipPosition().x,
            x_axis = chart.scales["x-axis-0"],
            leftX = x_axis.left,
            rightX = x_axis.right;

        ctx.save();
        // Line
        ctx.beginPath();
        ctx.moveTo(leftX - 20, y);
        ctx.lineTo(rightX, y);
        ctx.setLineDash([5, 5]);
        ctx.lineWidth = 1;
        ctx.strokeStyle = chart.options.scales.yAxes[0].gridLines.color;
        ctx.stroke();
        ctx.restore();
    }
};

export const horizontalBarValue = ({ chart, ctx, stacked, activeSegmentIndex }: any) => {
    ctx.font = "bold 11px Segoe UI";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = activeSegmentIndex != null && (activeSegmentIndex[0] != null && activeSegmentIndex[1] != null) ? "#f5f5f5" : "rgb(37, 36, 35)";
    if (stacked) {
        const meta = chart.controller.getDatasetMeta(
            chart.data.datasets.length - 1
        );
        meta.data.forEach((bar: any, index: number) => {
            let data = 0;
            chart.data.datasets.map((dataset: IChartDataSet) => {
                const value = dataset.data[index];
                if (typeof value === "number") {
                    return (data += value);
                }
            });

            if (activeSegmentIndex != null && (activeSegmentIndex[0] != null && activeSegmentIndex[1] != null)) {
                if (index === (activeSegmentIndex != null && activeSegmentIndex[0])) {
                    ctx.fillStyle = "rgb(37, 36, 35)";
                    ctx.fillText(chart.data.datasets[activeSegmentIndex[1]].data[activeSegmentIndex[0]], bar._model.x + 8, bar._model.y)
                } else {
                    ctx.fillStyle = "#f5f5f5";
                    ctx.fillText(data, bar._model.x + 8, bar._model.y);
                }
            } else {
                ctx.fillText(data, bar._model.x + 8, bar._model.y);
            }       
        });
    } else {
        chart.data.datasets.forEach((dataset: any, i: number) => {
            const meta = chart.controller.getDatasetMeta(i);
            meta.data.forEach((bar: any, index: number) => {
                const data = dataset.data[index];
                ctx.fillText(data, bar._model.x + 8, bar._model.y);
            });
        });
    }
};

export const chartConfig = ({
    type,
}: {
    type: "line" | "bar" | "horizontalBar" | "pie" | "bubble" | "lineAreaNoAxes";
}) => ({
    type,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1000,
        },
        layout: {
            padding: {
                left: 0,
                right: 16,
                top: 0,
                bottom: 0,
            },
        },
        scaleLabel: {
            display: false,
        },
        elements: {
            line: {
                tension: 0.4,
            },
        },
        hover: {
            mode: "dataset",
            intersect: false,
        },
        tooltips: tooltipConfig(),
        scales: {
            xAxes: [
                {
                    ticks: {
                        fontSize: 10,
                        padding: 0,
                        labelOffset: 4,
                        maxRotation: 0,
                        minRotation: 0,
                        callback: chartAxisCallback,
                    },
                    gridLines: {
                        borderDash: [5, 9999],
                        zeroLineBorderDash: [5, 9999],
                    },
                },
            ],
            yAxes: [
                {
                    stacked: false,
                    ticks: {
                        callback: chartAxisCallback,
                        fontSize: 10,
                        padding: -16,
                        labelOffset: 10,
                        maxTicksLimit: 5,
                    },
                    gridLines: {
                        lineWidth: 1,
                        drawBorder: false,
                        drawTicks: true,
                        tickMarkLength: 44,
                    },
                },
            ],
        },
    },
});

export const chartConfigPlugin = (tabKey: number, theme?: string) => ({
    type: "line",
    plugins: [ChartDataLabels],
    options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: function (context: any) {
                    return context.dataset.label == "Solicitações de Atendimento" && tabKey == 0;
                },
                formatter: function (value: any, ctx: any) {
                    return formatDataNumber(value);
                },
                anchor: 'end',
                align: 'top',
                color: theme == "dark" ? 'rgb(200, 198, 196)' : 'rgb(72, 70, 68)',
                font: {
                    weight: 'semibold'
                },
                offset: 0,
            },
        },
        animation: {
            duration: 1000,
        },
        layout: {
            padding: {
                left: 0,
                right: 16,
                top: 18,
                bottom: 0,
            },
        },
        scaleLabel: {
            display: false,
        },
        elements: {
            line: {
                tension: 0.4,
            },
        },
        hover: {
            mode: "dataset",
            intersect: false,
        },
        tooltips: tooltipConfig(),
        scales: {
            xAxes: [
                {
                    ticks: {
                        fontSize: 10,
                        padding: 0,
                        labelOffset: 4,
                        maxRotation: 0,
                        minRotation: 0,
                        maxTicksLimit: 8,
                        callback: chartAxisCallback,
                    },
                    gridLines: {
                        borderDash: [5, 9999],
                        zeroLineBorderDash: [5, 9999],
                    },
                },
            ],
            yAxes: [
                {
                    stacked: false,
                    ticks: {
                        callback: chartAxisCallback,
                        fontSize: 10,
                        padding: -16,
                        labelOffset: 10,
                        maxTicksLimit: 5,
                    },
                    gridLines: {
                        lineWidth: 1,
                        drawBorder: false,
                        drawTicks: true,
                        tickMarkLength: 44,
                    },
                },
            ],
        },
    },
});

export const pieChartConfig = () => ({
    type: "pie",
    options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1000,
        },
        layout: {
            padding: {
                left: 0,
                right: 16,
                top: 0,
                bottom: 0,
            },
        },
        scaleLabel: {
            display: false,
        },
        elements: {
            line: {
                tension: 0.4,
            },
        },
        hover: {
            mode: "dataset",
            intersect: false,
        },
        tooltips: tooltipConfig(),
        scales: {
            xAxes: [
                {
                    ticks: {
                        fontSize: 10,
                        padding: 0,
                        labelOffset: 4,
                        maxRotation: 0,
                        minRotation: 0,
                        callback: chartAxisCallback,
                    },
                    gridLines: {
                        borderDash: [5, 9999],
                        zeroLineBorderDash: [5, 9999],
                    },
                },
            ],
            yAxes: [
                {
                    stacked: false,
                    ticks: {
                        callback: chartAxisCallback,
                        fontSize: 10,
                        padding: -16,
                        labelOffset: 10,
                        maxTicksLimit: 5,
                    },
                    gridLines: {
                        lineWidth: 1,
                        drawBorder: false,
                        drawTicks: true,
                        tickMarkLength: 44,
                    },
                },
            ],
        },
    },
});

export const barChartConfigPlugin = ({
    type,
    selectedIndex,
    activeSegmentIndex,
    customDataLabel,
    stacked,
    theme,
}: {
    type: "bar" | "horizontalBar";
    selectedIndex: number | number[] | null,
    activeSegmentIndex?: number[] | number;
    customDataLabel?: boolean;
    stacked?: boolean;
    theme?: string;
}) => ({
    type,
    plugins: [ChartDataLabels],
    options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: type == "bar" ? true : false,
                formatter: function (value: any, ctx: any) {
                    if (!customDataLabel) {
                        return new Intl.NumberFormat('pt-BR').format(value)
                    } else {
                        const datasetArray = [];

                        if (selectedIndex && selectedIndex[0] != null && selectedIndex[1] != null) {
                            ctx.chart.data.datasets.forEach((dataset) => {
                                if (dataset.data[ctx.dataIndex] != undefined) {
                                    datasetArray.push(dataset.data[ctx.dataIndex]);
                                }
                            });

                            if (ctx.datasetIndex === datasetArray.length - 1) {
                                return datasetArray[selectedIndex[1]];
                            } else {
                                return '';
                            }
                        } else {
                            ctx.chart.data.datasets.forEach((dataset) => {
                                if (dataset.data[ctx.dataIndex] != undefined) {
                                    datasetArray.push(dataset.data[ctx.dataIndex]);
                                }
                            });

                            const totalSum = (total, datapoint) => {
                                return total + datapoint;
                            }

                            let sum = datasetArray.reduce(totalSum, 0);

                            if (ctx.datasetIndex === datasetArray.length - 1) {
                                return new Intl.NumberFormat('pt-BR').format(sum);
                            } else {
                                return '';
                            }
                        }
                    }
                },
                color: ((context: any, args: any) => {
                    const { dataIndex, datasetIndex, chart } = context;
                    const { datasets } = chart.config.data;

                    if (datasets.length > 1) {
                        if (selectedIndex && selectedIndex[0] == null && selectedIndex[1] == null) {
                            return theme === "dark" ? "rgb(200, 198, 196)" : "rgb(37, 36, 35)";
                        }

                        if (selectedIndex && selectedIndex[0] != null && selectedIndex[1] != null) {
                            if (stacked) {
                                if (selectedIndex[0] === dataIndex) {
                                    return theme === "dark" ? "rgb(200, 198, 196)" : "rgb(37, 36, 35)"
                                } else {
                                    return theme === "dark" ? "#525252" : "#f5f5f5";
                                }
                            } else {
                                if (selectedIndex[0] === dataIndex && selectedIndex[1] === datasetIndex) {
                                    return theme === "dark" ? "rgb(200, 198, 196)" : "rgb(37, 36, 35)"
                                } else {
                                    return theme === "dark" ? "#525252" : "#f5f5f5";
                                }
                            }
                        } else {
                            return theme === "dark" ? "rgb(200, 198, 196)" : "rgb(37, 36, 35)";
                        }
                    } else {
                        if (selectedIndex == null) {
                            return theme === "dark" ? "rgb(200, 198, 196)" : "rgb(37, 36, 35)";
                        }

                        const isSelected = selectedIndex === dataIndex;
                        return isSelected ? theme === "dark" ? "rgb(200, 198, 196)" : "rgb(37, 36, 35)" : theme === "dark" ? "#525252" : "#f5f5f5";
                    }
                }),
                anchor: 'end',
                align: 'end',
                font: {
                    weight: 'bold',
                    size: '10'
                },
                offset: 0,
            },
        },
        animation: {
            duration: 1,
        },
        layout: {
            padding: {
                top: 20,
                bottom: 0,
                left: 0,
                right: 0,
            },
        },
        scaleLabel: {
            display: false,
        },
        elements: {
            line: {
                tension: 0.4,
            },
        },
        hover: {
            mode: "dataset",
            intersect: false,
        },
        tooltips: tooltipConfig(),
        scales: {
            xAxes: [
                {
                    ticks: {
                        fontSize: 10,
                        padding: 0,
                        labelOffset: 4,
                        maxRotation: 0,
                        minRotation: 0,
                        maxTicksLimit: 8,
                        callback: chartAxisCallback,
                    },
                    gridLines: {
                        borderDash: [5, 9999],
                        zeroLineBorderDash: [5, 9999],
                    },
                },
            ],
            yAxes: [
                {
                    stacked: false,
                    ticks: {
                        callback: chartAxisCallback,
                        fontSize: 10,
                        padding: type == "bar" ? 10 : 0,
                        maxTicksLimit: type == "bar" ? 6 : 1000,
                        beginAtZero: type == "bar" ? true : false,
                        min: 0,
                    },
                    gridLines: {
                        lineWidth: 1,
                        drawBorder: false,
                        drawTicks: true,
                        tickMarkLength: 44,
                    },
                },
            ],
        },
    },
});

export const axesConfig = ({
    chart,
    ctx,
    colorScheme,
}: {
    chart: any;
    ctx: CanvasRenderingContext2D;
    colorScheme: any;
}) => {
    if (chart.config.type != 'pie') {
        const axesXGridLines = ctx!.createLinearGradient(100, 100, 100, 0);
        axesXGridLines.addColorStop(0.01, colorScheme.grey.border);
        axesXGridLines.addColorStop(0.01, "transparent");

        chart.options.scales.xAxes.forEach((xAxes: any, index: number) => {
            xAxes.ticks.fontColor = colorScheme.default.foreground2;
            if (index < 1) {
                xAxes.gridLines.color = axesXGridLines;
                xAxes.gridLines.zeroLineColor = axesXGridLines;
            } else {
                xAxes.gridLines.color = "transparent";
            }
        });
        chart.options.scales.yAxes.forEach((yAxes: any, index: number) => {
            yAxes.ticks.fontColor = colorScheme.default.foreground2;
            if (index < 1) {
                yAxes.gridLines.color = colorScheme.grey.border;
                yAxes.gridLines.zeroLineColor = colorScheme.grey.border;
            } else {
                yAxes.gridLines.color = "transparent";
            }
        });
    }
};

export const setTooltipColorScheme = ({
    chart,
    siteVariables,
    chartDataPointColors,
    patterns,
    verticalDataAlignment,
    customTooltip,
}: {
    chart: Chart;
    siteVariables: SiteVariablesPrepared;
    chartDataPointColors: string[];
    patterns?: IChartPatterns;
    verticalDataAlignment?: boolean;
    customTooltip?: boolean;
}) => {
    const { colorScheme, theme } = siteVariables;

    chart.options.tooltips = {
        ...chart.options.tooltips,
        backgroundColor: "black",
        borderColor: colorScheme.default.borderHover,
        multiKeyBackground: colorScheme.white.foreground,
        titleFontColor: colorScheme.default.foreground3,
        bodyFontColor: colorScheme.default.foreground3,
        footerFontColor: colorScheme.default.foreground3,
        borderWidth: 0,
        callbacks: {
            ...chart.options.tooltips?.callbacks,
            labelColor: (tooltipItem: any) => ({
                borderColor: "transparent",
                backgroundColor: customTooltip ?
                    chartDataPointColors[tooltipItem.index]
                    :
                    chartDataPointColors[
                    verticalDataAlignment
                        ? tooltipItem.index
                        : tooltipItem.datasetIndex
                    ],
            }),
        },
    };
};

export const tooltipConfig = () => ({
    yPadding: 4,
    xPadding: 12,
    caretPadding: 6,
    // Tooltip Title
    titleFontStyle: "200",
    titleFontSize: 20,
    // Tooltip Body
    bodySpacing: 4,
    bodyFontSize: 11.5,
    bodyFontStyle: "400",
    // Tooltip Footer
    footerFontStyle: "300",
    footerFontSize: 10,
    zIndex: 9999,

    callbacks: {
        title: (tooltipItems: any) => {
            const value = tooltipItems[0].yLabel;
            return typeof value === "number" && value > 999
                ? numberFormat(value)
                : value;
        },
        label: (tooltipItem: any, data: any) =>
            data.datasets[tooltipItem.datasetIndex].label,
        footer: (tooltipItems: any) => {
            const value = tooltipItems[0].xLabel;
            return typeof value === "number" && value > 999
                ? numberFormat(value)
                : value;
        },
    },
});
